import { Component, OnInit, HostBinding, ViewChild, ViewEncapsulation, ElementRef, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { RestService } from '../../../services/rest/rest.service';
import { CommonService } from '../../../services/common/common.service';
import { HelpIndex, Endpoint, TagOperationType, SaveToRemoveFromOperationType, ModalTargetType, UpdateStatusType, EntityType, MessageType, DREnumType } from '../../../models/enums';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { GenericGridComponent } from '../../generic-grid/generic-grid.component';
import { DetailsType, ReportDetails, ReportDetailsDefinitions, DocType, ScanHistory, ExceptionDetails } from '../../../models/report-details';
import { ReportType } from '../../../models/report';
import { GenericColDef, GridContext, SortByFilter, GridRowSelectionType, CellActionType, FilterTemplateType, CellTemplateType } from '../../../models/grid-context';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { ContextMenuOption } from '../../../models/context-menu-option';
import { TagsModalComponent, TagsModalData } from '../tags-modal/tags-modal.component';
import { AddToRemoveFromModalComponent, AddToRemoveFromModalData } from '../add-to-remove-from-modal/add-to-remove-from-modal.component';
import { RowNode } from 'ag-grid-community';
import * as _ from 'lodash';
import * as moment from 'moment';
import { DocumentFormat } from '../../../models/dr-document';
import { Permissions, ObjectActionListType } from '../../../models/permission';
import { Subscription } from 'rxjs/internal/Subscription';
import { ContextMenuComponent } from '../../context-menu/context-menu.component';
import { isNullOrUndefined } from '../../../models/utils';
import { AddSelectedTextToModalComponent, AddSelectedTextToModalData, AddSelectedTextToModalType } from '../add-selected-text-to-modal/add-selected-text-to-modal.component';
import { interval } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { TaskStatus } from '../../../models/task';

export interface ReportsDetailsModalData {
    detailType: DetailsType;
    handle: string;
    title: string;
    helpIndex: HelpIndex;
    reportDetails: any;
}

const oneDayTicks = 1000 * 60 * 60 * 24;

@Component({
    standalone: false,
    selector: 'res-reports-details-modal',
    templateUrl: './reports-details-modal.component.html',
    styleUrls: ['./reports-details-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class ReportsDetailsModalComponent extends BaseModalComponent implements OnInit, OnDestroy {
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;
    @HostBinding('class') classes;
    @ViewChild('downloadLink', { read: ElementRef, static: true }) downloadLink: ElementRef;
    @ViewChild('documentHeaderContextMenu') documentHeaderContextMenu: ContextMenuComponent;

    modalTitle = '';
    secondaryButtonLabel = 'Close';
    maximizable: false;
    modalHelpIndex: HelpIndex;
    modalAutomationId = 'reports-details';
    modalClass = 'reports-details-modal';
    isBusy = false;
    isBusyMessage = 'Loading...';
    menuBarType: DocType = DocType.FILETYPE;
    defaultDatePlaceholders = ['Start YYYY-MM-DD ...', 'End YYYY-MM-DD ...'];
    time = 'T00:00:00';
    startDate;
    endDate;
    hideWeekends = true;
    startIndex = 0;
    detailsGridData: ReportDetails;
    dateError = false;
    reviewFolderFilterQuery: string;
    hasDownloadPermission: boolean;
    hasNoData: boolean = false;

    columnDefs: Array<GenericColDef>;
    gridName: string;
    gridContext = new GridContext({
        sortBy: SortByFilter.COUNT,
        sortAscending: false,
        sortEnabled: true,
        inlineGridFilter: false,
    });
    documentHeaderButtonOptions: Array<ContextMenuOption>;
    rowSelection: GridRowSelectionType;
    requestInfo;
    private pollCompleteEvent: Subscription;
    originalFileTypeItems = [];
    getReportPoller: any;

    constructor(private modalService: BsModalService,
        private modalRef: BsModalRef,
        private restService: RestService,
        private commonService: CommonService,
        public changeDetector: ChangeDetectorRef) {
        super(modalService, modalRef);

        this.pollCompleteEvent = this.commonService.getMessage(MessageType.reloadExceptionDetailsListAfterPollComplete).subscribe(() => {
            if (this.genericGrid && (this.modalData?.detailType === DetailsType.EXCEPTION_DETAILS_IMPORT || this.modalData?.detailType === DetailsType.EXCEPTION_DETAILS)) {
                this.genericGrid.loadData();
            }
        })
    }

    ngOnInit() {
        this.setHelpIndex();
        this.rowSelection = this.setRowSelection();
        this.modalTitle = this.modalData.title;
        this.detailsGridData = new ReportDetails();
        this.startIndex = 0;
        this.hasDownloadPermission = Permissions.checkUserPermission(ObjectActionListType.DOCUMENT_REPORTS);
        if (this.modalData.detailType === DetailsType.ZERO_DAY_DETAILS) {
            try {
                this.startDate = this.modalData.startDate;
                this.endDate = this.modalData.endDate;
            } catch (e) {
                this.startDate = new Date();
                this.endDate = new Date();
            }
        }
        this.gridContext.inlineGridFilter = this.modalData && this.modalData.reportDetails && this.modalData.reportDetails.report &&
            this.modalData.reportDetails.report.reportType === ReportType.REVIEW_FOLDER ? true : false;
        this.getReportDetails();
    }

    getReportDetails(isGridRefresh = false): void {
        this.documentHeaderButtonOptions = this.modalData.reportDetails ? this.modalData.reportDetails.documentHeaderButtonOptions : null;

        // set header button menu options
        if (this.documentHeaderButtonOptions) {
            // filter header button menu options
            const reportType: ReportType = this.modalData?.reportDetails?.report?.reportType;
            if (!(reportType === ReportType.EMAIL_SENT_DOMAINS || reportType === ReportType.EMAIL_RECEIVED_DOMAINS)) {
                this.documentHeaderButtonOptions = this.documentHeaderButtonOptions.filter(option => option?.name?.localeCompare('Add to Domain List ...') !== 0);
            }

            this.documentHeaderButtonOptions.forEach(option => {
                if (!option.divider) {
                    option.click = () => this.onDocumentHeaderButtonOption(option.name);
                }
            });
        }

        if (this.modalData.detailType === DetailsType.EXCEPTION_DETAILS_IMPORT) {
            this.gridContext.inlineGridFilter = true;
            this.gridContext.sortBy = null;
            this.gridContext.sortEnabled = false;
            this.gridContext.onFilterApply = (queryStr: string, filterType: any, headerName: any) => this.onFilterApply(queryStr, filterType, headerName),
                this.getReportFilterData(false);
        } else {
            this.columnDefs = ReportDetailsDefinitions.get(DetailsType[DetailsType[this.modalData.detailType]])(this.menuBarType,
                (this.modalData.reportDetails ? this.modalData.reportDetails.report.reportType : null));
            const isGridFiltering = _.filter(this.columnDefs, (col) => col.field === 'name');
            if (isGridFiltering && this.gridContext && this.gridContext.inlineGridFilter) {
                isGridFiltering[0].filterType = FilterTemplateType.freeform;
                isGridFiltering[0].serviceTierFilter = true;
                this.gridContext.onFilterApply = (queryStr: string, filterType: any, headerName: any) => this.onFilterApply(queryStr, filterType, headerName);
                this.formatAndSetColDefs(this.reviewFolderFilterQuery, false);
            } else {
                this.formatAndSetColDefs(null, true, isGridRefresh);
            }
        }
    }

    getReportFilterData(onlyFiletype: boolean, handle?: string, filterValue?: string) {
        let auxParsingStatusEnums: Array<string>;
        const data = {
            viewHandle: handle ? handle : this.modalData.handle,
            reportRequests: [{
                reportType: ReportType.FILETYPE,
                cachedOnly: false
            }]
        };
        if (!onlyFiletype) {
            data.reportRequests.push({ reportType: ReportType.IMPORT_ERROR, cachedOnly: false });
        }
        this.isBusy = true;
        this.restService.post(Endpoint.realmManager_getDREnumNames, {
            enumType: DREnumType.AUX_PARSING_STATUS
        }).then(response => {
            auxParsingStatusEnums = response.names;
            if (filterValue) {
                this.getReport(data, onlyFiletype, auxParsingStatusEnums);
            } else {
                const params = {
                    selectAll: true,
                    hideSearchWb: true,
                    reportType: ReportType.IMPORT_ERROR,
                    searchDialect: this.commonService.searchDialect,
                    viewHandle: this.modalData.handle
                };
                this.restService.post(Endpoint.viewManager_drillDownOnReport, params).then(response => {
                    data.viewHandle = response.viewHandle;
                    if (response) {
                        this.getReport(data, onlyFiletype, auxParsingStatusEnums);
                    }
                }).catch(error => {
                    this.isBusy = false;
                    this.hasNoData = true;
                });
            }
        }).catch(error => {
            this.isBusy = false;
        });
    }

    getReport(data: any, onlyFiletype: boolean, auxParsingStatusEnums: Array<string>) {
        this.restService.post(Endpoint.viewManager_getReport, data).then(response => {
            if (response) {
                if (response.taskHandle) {
                    if (!this.getReportPoller) {
                        this.startReportsPoller(response.taskHandle, data, onlyFiletype, auxParsingStatusEnums);
                    }
                } else {
                    this.stopReportsPoller();
                    this.processFinalReportData(response, onlyFiletype, auxParsingStatusEnums);
                }
            }
        }).catch(error => {
            this.isBusy = false;
        });
    }

    getTasks(taskHandle: string, data: any, onlyFiletype: boolean, auxParsingStatusEnums: Array<string>) {
        this.restService.post(Endpoint.taskManager_getTasks, { taskHandles: [taskHandle] }).then(response => {
            if (response && response.tasks) {
                if (response.tasks[0].taskStatus === TaskStatus.SUCCESS) {
                    this.getReport(data, onlyFiletype, auxParsingStatusEnums);
                } else if (response.tasks[0].taskStatus === TaskStatus.FAILURE) {
                    this.stopReportsPoller();
                }
            }
        });
    }

    startReportsPoller(taskHandle: string, data: any, onlyFiletype: boolean, auxParsingStatusEnums: Array<string>) {
        let pollInterval = 5000; // 5 sec
        this.getReportPoller = interval(pollInterval).pipe(
            startWith(0)
        ).subscribe(() => this.getTasks(taskHandle, data, onlyFiletype, auxParsingStatusEnums));
    }

    stopReportsPoller() {
        if (this.getReportPoller) {
            this.getReportPoller.unsubscribe();
            this.getReportPoller = null;
        }
    }

    processFinalReportData(response: any, onlyFiletype: boolean, auxParsingStatusEnums: string[]) {
        this.isBusy = false;
        if (response && response.reports) {
            if (!this.columnDefs) {
                this.columnDefs = ReportDetailsDefinitions.get(DetailsType[DetailsType[this.modalData.detailType]])(this.menuBarType,
                    (this.modalData.reportDetails ? this.modalData.reportDetails.report.reportType : null));
                if (!this.hasDownloadPermission) {
                    const downloadColumn = this.columnDefs.find(f => f.cellTemplateType === CellTemplateType.download);
                    if (downloadColumn) {
                        downloadColumn.hide = true;
                    }
                }
            }
            const reasonColumn = this.columnDefs.find(f => f.field === 'reason'), fileTypeColumn = this.columnDefs.find(f => f.field === 'filetype'), auxparsingStatusColumn = this.columnDefs.find(f => f.field === 'auxparsingstatus');
            let filteredItems = [];
            if (!onlyFiletype) {
                filteredItems = [' '].concat((response.reports.find(f => f.reportType === ReportType.IMPORT_ERROR)).reportEntryList.map(t => t.name).sort(this.compare));
                reasonColumn.filterItems = filteredItems.filter(item => !auxParsingStatusEnums.includes(item));
                auxparsingStatusColumn.filterItems = [' '];
                auxparsingStatusColumn.filterItems = auxparsingStatusColumn.filterItems.concat(auxParsingStatusEnums);
                auxparsingStatusColumn.filterItems = auxparsingStatusColumn.filterItems.filter(item => filteredItems.includes(item));
            }
            fileTypeColumn.filterItems = [' '];
            fileTypeColumn.filterItems = fileTypeColumn.filterItems.concat((response.reports.find(f => f.reportType === ReportType.FILETYPE)).reportEntryList.map(t => t.name).sort(this.compare));
            if (!this.originalFileTypeItems || this.originalFileTypeItems.length == 0) {
                this.originalFileTypeItems = Object.assign(fileTypeColumn.filterItems);
            }
            this.formatAndSetColDefs(this.formatFilterValue(reasonColumn.filterItemSelected, fileTypeColumn.filterItemSelected, auxparsingStatusColumn.filterItemSelected), true);
        }
    }

    private compare(a: any, b: any) {
        const nameA = a.toLowerCase();
        const nameB = b.toLowerCase();
        if (nameA < nameB) {
            return -1;
        } else if (nameA > nameB) {
            return 1;
        }
        return 0;
    }

    onFilterApply(queryStr, filterType, headerName) {
        const reasonColumn = this.columnDefs.find(f => f.field === 'reason');
        const fileTypeColumn = this.columnDefs.find(f => f.field === 'filetype');
        const auxparsingStatusColumn = this.columnDefs.find(f => f.field === 'auxparsingstatus');
        this.startIndex = 0;
        if (this.modalData.reportDetails && this.modalData.reportDetails.report && this.modalData.reportDetails.report.reportType === ReportType.REVIEW_FOLDER) {
            this.reviewFolderFilterQuery = queryStr;
            this.formatAndSetColDefs(queryStr, false);
        } else if (filterType === FilterTemplateType.clear) {
            if (this.modalData.detailType === DetailsType.EXCEPTION_DETAILS_IMPORT) {
                if (headerName === 'File Type') {
                    reasonColumn.filterItemSelected = ' ';
                    fileTypeColumn.filterItemSelected = ' ';
                    fileTypeColumn.filterItems = this.originalFileTypeItems;
                    this.formatAndSetColDefs(null, true);
                }
            } else {
                this.formatAndSetColDefs(null, false);
            }
        } else if (headerName === 'Reason') {
            reasonColumn.filterItemSelected = queryStr;
            fileTypeColumn.filterItemSelected = ' ';
            auxparsingStatusColumn.filterItemSelected = auxparsingStatusColumn?.filterItemSelected !== 0 && auxparsingStatusColumn?.filterItemSelected?.length > 0 ? auxparsingStatusColumn.filterItemSelected : ' ';
            const params = {
                entryNameList: [reasonColumn.filterItemSelected, auxparsingStatusColumn.filterItemSelected],
                hideSearchWb: true,
                applyAndOperator: true,
                reportType: ReportType.IMPORT_ERROR,
                searchDialect: this.commonService.searchDialect,
                viewHandle: this.modalData.handle
            };
            this.columnDefs.forEach(c => c.automationIdCustom = true);
            if ((queryStr && queryStr !== ' ') || auxparsingStatusColumn.filterItemSelected?.length > 1) {
                this.isBusy = true;
                this.restService.post(Endpoint.viewManager_drillDownOnReport, params).then(response => {
                    this.isBusy = false;
                    if (response) {
                        const filterValue = 'parsingstatus:\"' + queryStr + '\"';
                        this.getReportFilterData(true, response.viewHandle, filterValue);
                    }
                }).catch(error => {
                    this.isBusy = false;
                });
            } else {
                this.getReportFilterData(false);
            }
        } else if (headerName === 'auxparsingstatus') {
            reasonColumn.filterItemSelected = reasonColumn?.filterItemSelected !== ' ' && reasonColumn?.filterItemSelected?.length > 0 ? reasonColumn?.filterItemSelected : ' ';
            fileTypeColumn.filterItemSelected = ' ';
            auxparsingStatusColumn.filterItemSelected = queryStr;
            const params = {
                entryNameList: [reasonColumn.filterItemSelected, auxparsingStatusColumn.filterItemSelected],
                hideSearchWb: true,
                applyAndOperator: true,
                reportType: ReportType.IMPORT_ERROR,
                searchDialect: this.commonService.searchDialect,
                viewHandle: this.modalData.handle
            };
            this.columnDefs.forEach(c => c.automationIdCustom = true);
            if ((queryStr && queryStr !== ' ') || (reasonColumn.filterItemSelected?.length > 1)) {
                this.isBusy = true;
                this.restService.post(Endpoint.viewManager_drillDownOnReport, params).then(response => {
                    this.isBusy = false;
                    if (response) {
                        const filterValue = 'auxparsingstatus:\"' + queryStr + '\"';
                        this.getReportFilterData(true, response.viewHandle, filterValue);
                    }
                }).catch(error => {
                    this.isBusy = false;
                });
            } else {
                this.getReportFilterData(false);
            }
        }
        else {
            fileTypeColumn.filterItemSelected = queryStr;
            auxparsingStatusColumn.filterItemSelected = auxparsingStatusColumn?.filterItemSelected !== 0 && auxparsingStatusColumn?.filterItemSelected?.length > 0 ? auxparsingStatusColumn.filterItemSelected : ' ';
            reasonColumn.filterItemSelected = reasonColumn?.filterItemSelected !== ' ' && reasonColumn?.filterItemSelected?.length > 0 ? reasonColumn?.filterItemSelected : ' ';
            this.columnDefs.forEach(c => c.automationIdCustom = true);
            this.formatAndSetColDefs(this.formatFilterValue(reasonColumn.filterItemSelected, fileTypeColumn.filterItemSelected, auxparsingStatusColumn.filterItemSelected), true);
        }
    }

    formatAndSetColDefs(value?: string, setColDefs = true, isGridRefresh = false, needsGridReady = true) {
        this.requestInfo = this.formatReportDetailsRequest(value);
        if (this.genericGrid && this.genericGrid.pager) {
            if (setColDefs) {
                this.genericGrid.headerContextMenuOptions = [];
                if (!isGridRefresh) {
                    this.genericGrid.setColumnDefs(this.columnDefs);
                }
                if (needsGridReady) {
                    // prevent loadData from onGridReady when called from here, onGridReady is used here for setting grid column headers
                    this.genericGrid.gridOptions.onGridReady({ api: null, columnApi: null, type: 'reportDetailsGridReady', context: null });
                }
            }
            this.genericGrid.pager.startIndex = this.startIndex;
            this.genericGrid.pager.currentPage = (Math.ceil(this.genericGrid.pager.startIndex / 100)) + 1;
            this.genericGrid.pager.endIndex = (this.startIndex + 100) < this.genericGrid.pager.totalItems ? this.startIndex + 100 : this.genericGrid.pager.totalItems;
            setTimeout(() => {
                this.genericGrid.loadData();
            });
        }
    }

    formatFilterValue(reason, fileType, auxParsingStatus) {
        let filterValue = null;

        if (fileType && fileType !== ' ' && reason && reason !== ' ' && auxParsingStatus && auxParsingStatus !== ' ') {
            filterValue = 'parsingstatus:\"' + reason + '\" AND filetype:\"' + fileType + '\"' + ' AND auxparsingstatus:\"' + auxParsingStatus + '\"';
        } else if (fileType && fileType !== ' ' && reason && reason !== ' ') {
            filterValue = 'parsingstatus:\"' + reason + '\" AND filetype:\"' + fileType + '\"';
        } else if (fileType && fileType !== ' ' && auxParsingStatus && auxParsingStatus !== ' ') {
            filterValue = 'auxparsingstatus:\"' + auxParsingStatus + '\" AND filetype:\"' + fileType + '\"';
        } else if (auxParsingStatus && auxParsingStatus !== ' ' && reason && reason !== ' ') {
            filterValue = 'auxparsingstatus:\"' + auxParsingStatus + '\" AND parsingstatus:\"' + reason + '\"';
        } else if (fileType && fileType !== ' ') {
            filterValue = 'filetype:\"' + fileType + '\" AND NOT parsingstatus:\"SUCCESS\"';
        } else if (reason && reason !== ' ') {
            filterValue = 'parsingstatus:\"' + reason + '\"';
        } else if (auxParsingStatus && auxParsingStatus !== ' ') {
            filterValue = 'auxparsingstatus:\"' + auxParsingStatus + '\"';
        }
        return filterValue;
    }

    responseArrayCallback = (value: any) => {
        if (value && value.size) {
            value.size = this.commonService.formatSizeUnits(value.size);
        }
        return value;
    }

    responseCallback = (response: any) => {
        switch (this.modalData.detailType) {
            case DetailsType.SCAN_HISTORY: {
                if (response?.events) {
                    response.data = (<Array<any>>response.events).map(d => new ScanHistory(d));
                    response.totalCount = response.events.length;
                }
                break;
            }
            case DetailsType.EXCEPTION_DETAILS_IMPORT: {
                if (response) {
                    if (response?.documents) {
                        response.data = (<Array<any>>response.documents).map(d => new ExceptionDetails(d));
                        response.totalCount = response.view.documentCount;
                    }
                    if (response?.view?.documentCount === 0) {
                        this.hasNoData = true;
                    }
                }
                break;
            }
            default: {
                const responseReport = response?.reports?.length > 0 ? response.reports[0] : null;
                if (responseReport) {
                    response.data = responseReport.reportEntryList;
                    response.totalCount = responseReport.totalCount;
                }
                break;
            }
        }
    }

    formatReportDetailsRequest(filterValue = null): DrRequestInfo {
        let preparedRequest: DrRequestInfo;
        if (this.modalData.reportDetails && this.modalData.reportDetails.report && this.modalData.reportDetails.report.reportType === ReportType.REVIEW_FOLDER) {
            preparedRequest = new DrRequestInfo({
                endpoint: Endpoint.viewManager_filterReviewFolderReport,
                responseName: 'data',
                data: {
                    filters: [{ attribute: 'name', value: filterValue && filterValue.length > 0 ? filterValue : '', operator: 'LIKE' }],
                    viewHandle: this.modalData.handle,
                    count: 100,
                    startIndex: this.startIndex,
                    sortBy: SortByFilter.COUNT,
                    sortAscending: false,
                }
            });
        } else if (this.modalData.detailType === DetailsType.SCAN_HISTORY) {
            preparedRequest = new DrRequestInfo({
                endpoint: Endpoint.corpusManager_listScanHistoryEvents,
                responseName: 'data',
                data: {
                    corpusHandle: this.modalData.handle
                }
            });
        } else if (this.modalData.detailType === DetailsType.EXCEPTION_DETAILS_IMPORT) {
            preparedRequest = new DrRequestInfo({
                endpoint: Endpoint.corpusManager_listImportExceptions,
                responseName: 'data',
                data: {
                    viewHandle: this.modalData.handle,
                    count: 100,
                    filters: [],
                    selectedAttributes: ['docnum', 'importpath', 'parsingstatus', 'filetype', 'datescanned', 'size', 'auxparsingstatus'],
                    startIndex: this.startIndex,
                    sortBy: null,
                }
            });
            if (filterValue) {
                preparedRequest.data.filters.push({ attribute: 'viewlistattribute', operator: 'EQUALS', value: `${filterValue}` });
            }
        } else {
            preparedRequest = new DrRequestInfo({
                endpoint: Endpoint.viewManager_getReport,
                responseName: 'data',
                data: {
                    viewHandle: this.modalData.handle,
                    facetFilters: null,
                    dateReportRequests: [],
                    reportRequests: []
                }
            });
            switch (this.modalData.reportDetails.report.reportType) {
                case ReportType.DATE_EMAIL_RECEIVED:
                case ReportType.DATE_PRIMARY:
                case ReportType.DATE_EMAIL_SENT: {
                    this.gridContext.sortBy = SortByFilter.NAME;
                    this.gridContext.sortAscending = true;
                    const dateReportRequest = {
                        cachedOnly: false,
                        count: 100,
                        endDate: this.endDate ? this.endDate.getFullYear() + '-' + (this.endDate.getMonth() + 1) + '-' + this.endDate.getDate() : '',
                        granularity: 'DAY',
                        reportType: this.modalData.reportDetails.report.reportType,
                        sortAscending: true,
                        sortBy: SortByFilter.NAME.toString(),
                        startDate: this.startDate ? this.startDate.getFullYear() + '-' + (this.startDate.getMonth() + 1) + '-' + this.startDate.getDate() : '',
                        startIndex: this.startIndex,
                        filters: []
                    };
                    if (this.modalData.detailType === DetailsType.ZERO_DAY_DETAILS) {
                        dateReportRequest.filters.push({ attribute: 'zeroday' });
                        if (this.hideWeekends) {
                            dateReportRequest.filters.push({ attribute: 'suppressweekenddays', value: 'suppressweekenddays' });
                        }
                    }
                    preparedRequest.data.dateReportRequests.push(dateReportRequest);
                    break;
                }
                case ReportType.BILLING_REPORT:
                    preparedRequest.data.reportRequests.push({
                        cachedOnly: false,
                        reportType: this.modalData.reportDetails.report.reportType,
                        attributes: [],
                        count: 100,
                        sortAscending: false,
                        sortBy: SortByFilter.COUNT,
                        startIndex: this.startIndex
                    });
                    break;
                case ReportType.IMPORT_ERROR:
                case ReportType.CUSTOM_WARNINGS_ERRORS:
                case ReportType.FILETYPE: {
                    const dateReportRequest = {
                        cachedOnly: false,
                        count: 100,
                        reportType: this.modalData.detailType === DetailsType.EXCEPTION_DETAILS ? this.menuBarType : this.modalData.reportDetails.report.reportType,
                        sortAscending: false,
                        sortBy: SortByFilter.COUNT,
                        startIndex: this.startIndex,
                    };

                    if (dateReportRequest.reportType === DocType.DOCEXT || dateReportRequest.reportType === DocType.DOCEXT_CONFLICT) {
                        // polling is needed for these 2 report types when the backend sends a taskhandle, adding report type in the requestHandle in order to know when to poll
                        preparedRequest.data.requestHandle = dateReportRequest.reportType;
                    }
                    preparedRequest.data.reportRequests.push(dateReportRequest);
                    break;
                }
                default:
                    preparedRequest.data.reportRequests.push({
                        cachedOnly: false,
                        reportType: this.modalData.reportDetails.report.reportType,
                        sortAscending: false,
                        sortBy: SortByFilter.COUNT,
                        count: 100,
                        startIndex: this.startIndex,
                    });
            }
        }
        return preparedRequest;
    }

    gridPagerCustomCallback = (startIndex: any) => {
        if (startIndex !== undefined) {
            this.startIndex = startIndex;
            if (this.modalData.detailType === DetailsType.EXCEPTION_DETAILS_IMPORT) {
                if (this.requestInfo) {
                    this.requestInfo.data.startIndex = startIndex;
                    this.genericGrid.loadData();
                }
            } else {
                this.getReportDetails(true);
            }
        }
    }

    compareDateRange(): void {
        if ((this.startDate && this.endDate) && this.endDate.getTime() < this.startDate.getTime()) {
            this.dateError = true;
        } else {
            this.dateError = false;
        }
        this.changeDetector.detectChanges();
    }

    getReportDetailsForPeriod(mode) {
        this.startIndex = 0;
        if (this.genericGrid && this.genericGrid.pager) {
            this.genericGrid.pager.startIndex = 0;
            this.genericGrid.pager.currentPage = 1;
            this.genericGrid.pager.endIndex = 100;
        }
        const granularity = this.computeGranularity();
        const ms = this.endDate.getTime() - this.startDate.getTime();
        const duration = moment.duration(moment(this.endDate).diff(moment(this.startDate)));
        const daysInFebMoth = new Date(this.endDate.getYear(), 1, 1);
        if (mode === 'next') {
            if (ms > oneDayTicks) {
                const startTime = moment(this.startDate).add(duration.asDays(), 'days');
                const endTime = moment(this.endDate).add((this.startDate.getDate() === 1 && this.endDate.getDate() === 31 && this.endDate.getMonth() === 0) ?
                    moment(daysInFebMoth).daysInMonth() : duration.asDays(), 'days');

                startTime.add(1, 'days').set('hours', 0).set('minutes', 0).set('seconds', 0);
                const endTimeMonthDate = moment(endTime.creationData().input);

                if (granularity === 'DAY' && endTimeMonthDate.daysInMonth() === this.endDate.getDate() && this.startDate.getDate() === 1) {
                    startTime.startOf('month');
                    endTime.endOf('month');
                } else {
                    endTime.add(1, 'days').set('hours', 0).set('minutes', 0).set('seconds', 0);
                }

                this.setNewDate(startTime, endTime);
            } else {
                this.setNewDate(moment(this.startDate).add(ms === 0 ? 1 : 2, 'days'), moment(this.endDate).add(ms === 0 ? 1 : 2, 'days'));
            }
        } else if (mode === 'previous') {
            if (ms > oneDayTicks) {
                const startTime = moment(this.startDate).subtract((this.startDate.getDate() === 1 && this.endDate.getDate() === 31 && this.endDate.getMonth() === 2
                    && this.startDate.getMonth() === 2) ? moment(daysInFebMoth).daysInMonth() : duration.asDays(), 'days');
                const endTime = moment(this.endDate).subtract(duration.asDays(), 'days');

                endTime.subtract(1, 'days').set('hours', 0).set('minutes', 0).set('seconds', 0);
                const endTimeMonthDate = moment(endTime.creationData().input);

                if (granularity === 'DAY' && endTimeMonthDate.daysInMonth() === this.endDate.getDate() && this.startDate.getDate() === 1) {
                    startTime.startOf('month');
                    endTime.endOf('month');
                } else {
                    startTime.subtract(1, 'days').set('hours', 0).set('minutes', 0).set('seconds', 0);
                }

                this.setNewDate(startTime, endTime);
            } else {
                this.setNewDate(moment(this.startDate).subtract(ms === 0 ? 1 : 2, 'days'), moment(this.endDate).subtract(ms === 0 ? 1 : 2, 'days'));
            }
        }
        this.getReportDetails();
    }

    setNewDate(start, end): void {
        this.startDate = new Date(moment(start).format('YYYY-MM-DD') + this.time);
        this.endDate = new Date(moment(end).format('YYYY-MM-DD') + this.time);
        this.changeDetector.detectChanges();
    }


    computeGranularity(): string {
        let granularity = 'YEAR';

        if (this.startDate <= this.endDate) {
            const diff = this.dateDiffInDays(this.startDate, this.endDate);
            if (diff < 31) {
                granularity = 'DAY';
            } else if (diff >= 31 && diff < 540) {
                granularity = 'MONTH';
            }
        }
        return granularity;
    }

    dateDiffInDays(a, b): any {
        const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
        const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

        return Math.floor((utc2 - utc1) / oneDayTicks);
    }

    public downloadReport(dataHandle?: string): void {
        let endpoint = Endpoint.viewManager_retrieveReport;
        let postParams;

        if (this.modalData.detailType === DetailsType.ZERO_DAY_DETAILS) {
            postParams = {
                handle: this.modalData.handle,
                dateReportRequest: {
                    cachedOnly: false,
                    reportType: this.modalData.reportDetails.report.reportType,
                    startDate: this.startDate ? this.startDate.getFullYear() + '-' + (this.startDate.getMonth() + 1) + '-' + this.startDate.getDate() : '',
                    endDate: this.endDate ? this.endDate.getFullYear() + '-' + (this.endDate.getMonth() + 1) + '-' + this.endDate.getDate() : '',
                    granularity: 'DAY',
                    filters: !this.hideWeekends ? [{ attribute: 'zeroday' }] : [{ attribute: 'zeroday' }, { attribute: 'suppressweekenddays', value: 'true', operator: 'EQUALS' }]
                }
            };
        } else if (this.modalData.detailType === DetailsType.EXCEPTION_DETAILS_IMPORT) {
            if (dataHandle) {
                endpoint = Endpoint.documentManager_retrieveDocument;
                postParams = {
                    documentHandle: dataHandle,
                    enablePersistentHighlighting: false,
                    format: DocumentFormat.NATIVE,
                    requestHandle: dataHandle
                };
            } else {
                const reasonColumn = this.columnDefs?.find(f => f.field === 'reason'),
                    fileTypeColumn = this.columnDefs?.find(f => f.field === 'filetype'),
                    auxParsingStatusColumn = this.columnDefs?.find(f => f.field === 'auxparsingstatus'),
                    filterValue = this.formatFilterValue(reasonColumn?.filterItemSelected, fileTypeColumn?.filterItemSelected, auxParsingStatusColumn?.filterItemSelected);
                endpoint = Endpoint.corpusManager_getImportExceptionsUrl;
                postParams = {
                    handle: this.modalData.handle,
                    requestHandle: this.modalData.handle,
                    selectedAttributes: []
                };
                if (filterValue) {
                    postParams.selectedAttributes.push('viewlistattribute=' + filterValue);
                }
            }
        } else {
            postParams = {
                handle: this.modalData.handle,
                reportRequest: {
                    attributes: [{
                        // csv extension for document type details modal on warnings & errors (IMPORT_ERROR) tabs as it's a special case,
                        // predifined extensions from report model (downloadReportExtension) for other details modal
                        name: this.modalData.detailType === DetailsType.EXCEPTION_DETAILS && this.modalData.reportDetails.report.reportType === ReportType.IMPORT_ERROR ? 'csv' :
                            (this.modalData.reportDetails && this.modalData.reportDetails.report && this.modalData.reportDetails.report.downloadReportExtension) ?
                                this.modalData.reportDetails.report.downloadReportExtension : 'csv', value: 'true'
                    }],
                    reportType: this.modalData.detailType === DetailsType.EXCEPTION_DETAILS ? this.menuBarType : this.modalData.reportDetails.report.reportType
                }
            };
        }
        this.isBusy = true;
        this.restService.post(endpoint, postParams).then(response => {
            this.isBusy = false;
            if (response) {
                if (response) {
                    const url = `/` + response.url;

                    const filter = this.reviewFolderFilterQuery && this.reviewFolderFilterQuery.length > 0 &&
                        this.modalData.reportDetails.report.reportType === ReportType.REVIEW_FOLDER ? true : false;
                    const reportName = this.modalData.detailType === DetailsType.EXCEPTION_DETAILS ? this.menuBarType :
                        this.modalData.detailType === DetailsType.ZERO_DAY_DETAILS ? 'ZERO_DAY' :
                            this.modalData.detailType === DetailsType.EXCEPTION_DETAILS_IMPORT ? 'ScanReport' : this.modalData.reportDetails.report.reportType;
                    this.commonService.unzipFile(url, this.downloadLink.nativeElement, reportName, filter, this.reviewFolderFilterQuery);
                }
            }
        }, error => {
            this.isBusy = false;
            console.error('Failed to download!');
        });
    }

    tabsBarVisible(): boolean {
        return this.modalData.detailType === DetailsType.EXCEPTION_DETAILS;
    }

    setBarType(value): void {
        this.menuBarType = value;
        if (this.genericGrid && this.genericGrid.gridContext) {
            this.genericGrid.gridContext.sortBy = SortByFilter.COUNT;
            this.genericGrid.gridContext.sortAscending = false;
        }
        this.getReportDetails();
    }

    cellClickedCallback = (rowNode: RowNode, action: CellActionType) => {
        if (rowNode && rowNode.data && rowNode.data.handle && action) {
            switch (action) {
                case CellActionType.linkLabelClick: {
                    this.downloadReport(rowNode.data.handle);
                    break;
                }
            }
        }
    }

    hideExceptionExtensionTab(): boolean {
        if (this.modalData.reportDetails.report.reportType === ReportType.FILETYPE) {
            return this.modalData.reportDetails.report.node.entityType !== EntityType.import;
        }
        return true;
    }

    onDocumentHeaderButton(event: MouseEvent): void {
        const node = {
            contextMenuOptions: this.documentHeaderButtonOptions,
            automationId: this.modalAutomationId
        }
        this.genericGrid.onContextMenu(this.documentHeaderContextMenu, event, node, event.currentTarget);
    }

    loadSelectedDomains(): Promise<string[]> {
        if (this.genericGrid.allRowsSelected) {
            return new Promise<string[]>(resolve => {
                this.isBusy = true;
                this.isBusyMessage = `Loading All ${this.genericGrid.totalItems.toLocaleString()} domains ...`;
                const payload = {
                    viewHandle: this.modalData.handle,
                    facetFilters: null,
                    dateReportRequests: [],
                    reportRequests: [{
                        cachedOnly: false,
                        count: -1,
                        reportType: this.modalData.reportDetails.report.reportType,
                        sortAscending: false,
                        sortBy: SortByFilter.COUNT,
                        startIndex: -1
                    }]
                };
                this.restService.post(Endpoint.viewManager_getReport, payload).then(response => {
                    this.isBusy = false;
                    if (response && response.reports) {
                        resolve(response?.reports[0]?.reportEntryList.map(entry => entry.name));
                    }
                }).catch(error => {
                    this.isBusy = false;
                });
            });
        } else {
            this.isBusy = false;
            return Promise.resolve(this.genericGrid.selectedRows.map(r => r.name));
        }
    }

    onDocumentHeaderButtonOption(action: string): void {
        this.isBusy = true;
        const reportType = this.modalData.detailType === DetailsType.EXCEPTION_DETAILS ? this.menuBarType : this.modalData.reportDetails.report.reportType;
        // handle header button option actions here
        let itemsSelected = [];
        if (this.genericGrid.allRowsSelected && (this.reviewFolderFilterQuery === '' || isNullOrUndefined(this.reviewFolderFilterQuery))) {
            itemsSelected = null;
        }
        else {
            itemsSelected = this.genericGrid.selectedRows.map(function (elem) {
                const blockSize = reportType === DocType.DOCEXT_CONFLICT ? `/${elem.blockSize}` : '';
                return (elem.name || elem.filetype || elem.fileextension || elem.current) + blockSize;
            })
        }
        const params = {
            entryNameList: itemsSelected,
            granularity: 'DAY',
            reportType: reportType,
            searchDialect: this.commonService.searchDialect,
            selectAll: this.reviewFolderFilterQuery && this.genericGrid.allRowsSelected ? false : this.genericGrid.allRowsSelected,
            viewHandle: this.modalData.handle
        };

        if (action?.localeCompare('Add to Domain List ...') === 0) {
            this.loadSelectedDomains().then(selectedDomains => {
                this.commonService.openModal(AddSelectedTextToModalComponent, {
                    modalData: <AddSelectedTextToModalData>{
                        type: AddSelectedTextToModalType.DomainList,
                        selectedDomains: selectedDomains
                    },
                    class: 'vertical-percent-100 horizontal-percent-100'
                });
            })
        } else {
            this.restService.post(Endpoint.viewManager_drillDownOnReport, params).then(response => {
                this.isBusy = false;
                if (response) {
                    switch (action) {
                        case 'Add Tags ...': {
                            this.commonService.openModal(TagsModalComponent, {
                                modalData: <TagsModalData>{
                                    targets: [response],
                                    targetType: ModalTargetType.report,
                                    operationType: TagOperationType.tag,
                                    updateStatusType: UpdateStatusType.WORKING_SET
                                }
                            });
                            break;
                        }
                        case 'Remove Tags ...': {
                            this.commonService.openModal(TagsModalComponent, {
                                modalData: <TagsModalData>{
                                    targets: [response],
                                    targetType: ModalTargetType.report,
                                    operationType: TagOperationType.untag,
                                    updateStatusType: UpdateStatusType.WORKING_SET
                                }
                            });
                            break;
                        }
                        case 'Add to ...': {
                            this.commonService.openModal(AddToRemoveFromModalComponent, {
                                modalData: <AddToRemoveFromModalData>{
                                    targets: [response],
                                    operationType: SaveToRemoveFromOperationType.add,
                                    targetType: ModalTargetType.report,
                                    updateStatusType: UpdateStatusType.WORKING_SET
                                }, class: 'horizontal-px-700'
                            });
                            break;
                        }
                        case 'Remove from ...': {
                            this.commonService.openModal(AddToRemoveFromModalComponent, {
                                modalData: <AddToRemoveFromModalData>{
                                    targets: [response],
                                    operationType: SaveToRemoveFromOperationType.remove,
                                    targetType: ModalTargetType.report,
                                    updateStatusType: UpdateStatusType.WORKING_SET
                                }, class: 'horizontal-px-700'
                            });
                            break;
                        }
                        default: console.log('Unkwown action attempted: ' + action);
                    }
                }
            }).catch(error => {
                this.isBusy = false;
            });
        }
    }

    selectedItems(): string {
        if (this.genericGrid) {
            const count = this.genericGrid.selectedRows.length;
            if (count > 0) {
                if (this.genericGrid.allRowsSelected) {
                    return `All ${this.genericGrid.totalItems.toLocaleString()}`;
                }
                return `${count.toLocaleString()} on this page`;
            }
            return `${count.toLocaleString()}`;
        }
        return '0';
    }

    setHelpIndex = () => {
        this.modalHelpIndex = this.modalData.helpIndex;
        if (this.modalData.reportDetails) {
            switch (this.modalData.reportDetails.report.reportType) {
                case ReportType.METADATA_BULK_SEARCH_SUMMARY:
                case ReportType.FILETYPE:
                    this.modalHelpIndex = HelpIndex.details_multiple_tabs_from_report_dlg;
                    break;
                case ReportType.BILLING_REPORT:
                    this.modalHelpIndex = HelpIndex.case_billing_summary_filetype_details_dlg;
                    break;
                default:
                    if (this.modalData.detailType === DetailsType.EXCEPTION_DETAILS) {
                        this.modalHelpIndex = HelpIndex.details_multiple_tabs_from_report_dlg;
                    } else if (this.modalData.detailType === DetailsType.STANDARD_DETAILS) {
                        this.modalHelpIndex = HelpIndex.details_single_tab_from_report_dlg;
                    } else if (this.modalData.detailType === DetailsType.ZERO_DAY_DETAILS) {
                        this.modalHelpIndex = HelpIndex.myedisc_zerodayreport_dlg;
                    } else {
                        this.modalHelpIndex = HelpIndex.details_single_tab_from_report_dlg;
                    }
                    break;
            }
        }
    }

    setRowSelection(): GridRowSelectionType {
        switch (this.modalData.detailType) {
            case DetailsType.SCAN_HISTORY:
            case DetailsType.EXCEPTION_DETAILS_IMPORT:
            case DetailsType.ZERO_DAY_DETAILS:
                return GridRowSelectionType.none;
            default:
                return GridRowSelectionType.multipleCheckboxWithRowClickSelection;
        }
    }

    close() {
        this.closeModal(true);
    }

    ngOnDestroy(): void {
        if (this.pollCompleteEvent) {
            this.pollCompleteEvent.unsubscribe();
        }
        setTimeout(() => {
            this.stopReportsPoller();
        }, 8000);
    }

}
